import AppCommandBar from 'app/AppCommandBar'
import { AccountContext } from 'auth'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import { WikiForm, WikiFormValues } from 'forms/WikiForm/WikiForm'
import {
  useCreateCommand,
  useCustomSearchCommand,
  useDeleteCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import { pick } from 'lodash'
import { CheckType, UserRole, Wiki, WikiUpdateInput } from 'model'
import {
  IStackTokens,
  SelectionMode,
  SelectionZone,
  Stack,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import {
  useCreateWikiMutation,
  useDeleteWikisMutation,
  useReadWikisQuery,
  useUpdateWikiCheckMutation,
  useUpdateWikiMutation,
} from './AdminWikiView.generated'
import { WikiListSingleView } from './WikiListSingleView'

export const AdminWikiView: React.FC = () => {
  const { t } = useTranslation()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [createWiki] = useCreateWikiMutation()
  const [updateWiki] = useUpdateWikiMutation()
  const [deleteWikis] = useDeleteWikisMutation()
  const [dialogState, showDialog, hideDialog] = useDialogState()

  const [filesUploading, setFilesUploading] = useState(false)
  const router = useRouter()
  const paramsNew = router.getState()
  const newElementsCount = paramsNew.params.wikiCounter || 0
  const [updateWikiCheck] = useUpdateWikiCheckMutation()

  const currentMicrosoftId: { homeAccount: string } =
    React.useContext(AccountContext)
  // const modifiedWikisCounter = useNewWikiContext()

  const [selection, selectedItems] = useSelection<Wiki>({
    getKey: (item) => item._id,
  })
  const [selectedItemId, setSelectedItemId] = React.useState()

  const [routeParams, updateRouteParams] = useRouteParams('admin.data.wiki', {
    search: undefined,
  })
  const { loading, data, error, refetch } = useReadWikisQuery({
    variables: { search: routeParams.search },
  })
  React.useEffect(() => {
    const updateAsync = async () => {
      await updateWikiCheck({
        variables: {
          update: {
            user: currentMicrosoftId.homeAccount,
            type: CheckType.Wiki,
          },
        },
      })
    }
    updateAsync()
    // modifiedWikisCounter.setRefetchFlag(oldValue => !oldValue)
    // WARNING! Do NOT put modifiedWikisCounter in dependency array.
    // Page will do infinite requests when you do this!
  }, [currentMicrosoftId.homeAccount, updateWikiCheck])

  const wikisUnsorted: Wiki[] =
    !loading && data && data.wikis ? (data.wikis as Wiki[]) : []

  const wikis = wikisUnsorted.sort((a: Wiki, b: Wiki) => {
    const firstDate = a.created! as Date
    const secondDate = b.created! as Date
    const result = firstDate > secondDate ? -1 : firstDate < secondDate ? 1 : 0
    return result
  })
  const wikiList = wikis.map((elem, index) => {
    return { ...elem, key: `${elem._id}` }
  })

  selection.setItems(wikiList, false)

  const verticalGapStackTokens: IStackTokens = {
    childrenGap: '2vh',
    padding: '1vh',
  }

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.wiki.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )
  useCreateCommand(
    'wiki',
    {
      priority: 20,
      onClick: () => {
        setSelectedItemId(undefined)
        showPanel()
      },
    },
    [selectedItems]
  )
  const handleItemEdit = () => {
    const item = selectedItems[0]
    setSelectedItemId(item._id)
    const existingItem = {
      ...selectedItems[0],
      provingGround: item.provingGround ? item.provingGround._id : undefined,
    } as WikiUpdateInput
    showPanel(existingItem)
  }

  useEditCommand(
    { name: 'wiki', count: selectedItems.length },
    {
      priority: 20,
      onClick: handleItemEdit,
    },
    [selectedItems]
  )

  useDeleteCommand(
    { name: 'wiki', count: selectedItems.length },
    {
      priority: 30,
      onClick: showDialog,
    },
    [selectedItems]
  )

  const handleSubmit = React.useCallback(
    async (values: WikiFormValues, formikActions: FormikActions<any>) => {
      formikActions.setSubmitting(true)
      setFilesUploading(true)
      if (!values.provingGround) {
        delete values.provingGround
      }

      if (selectedItemId) {
        await updateWiki({
          variables: {
            id: selectedItemId,
            update: pick(values, [
              'topic',
              'provingGround',
              'inactive',
              'tag',
              'body',
              'files',
              'uploadFiles',
              'uploadPreview',
            ]),
          },
        })
      } else {
        await createWiki({
          variables: {
            input: pick(values, [
              'topic',
              'provingGround',
              'inactive',
              'tag',
              'body',
              'uploadFiles',
              'uploadPreview',
            ]),
          },
        })
      }
      setFilesUploading(false)
      formikActions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hidePanel()
    },
    [
      updateWiki,
      createWiki,
      selection,
      refetch,
      hidePanel,
      selectedItemId,
      setFilesUploading,
    ]
  )

  const handleConfirmDelete = React.useCallback(async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((ft) => ft._id)
      await deleteWikis({
        variables: { ids },
      })
    }
    hideDialog()
    refetch()
  }, [selectedItems, deleteWikis, refetch, hideDialog])

  return (
    <ViewAuthorizer
      roles={[UserRole.ResourceManager, UserRole.SystemAdministrator]}
    >
      <FlexSpinner loading={loading} error={error}>
        <StickyPane>
          <AppCommandBar />
          <SelectionZone selection={selection}>
            <Stack
              wrap={true}
              horizontal={true}
              tokens={verticalGapStackTokens}
            >
              {wikiList.map((wikiElement, index) => {
                return (
                  <WikiListSingleView
                    newElement={index < newElementsCount ? true : false}
                    key={wikiElement.key}
                    item={wikiElement}
                    itemIndex={index}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                    onShowHandler={handleItemEdit}
                  />
                )
              })}
            </Stack>
          </SelectionZone>
        </StickyPane>

        <FormikPanel
          {...panelState}
          onSubmit={handleSubmit}
          headerText={
            selectedItemId ? `${t('model.wiki.edit')} ` : t('model.wiki.create')
          }
        >
          <WikiForm disabled={false} filesUploading={filesUploading} />
        </FormikPanel>
        <ConfirmDeleteDialog
          {...dialogState}
          onConfirm={handleConfirmDelete}
          itemCount={selectedItems.length}
        />
      </FlexSpinner>
    </ViewAuthorizer>
  )
}
